<template>
  <v-row>
    <v-col class="px-0">
      <v-card outlined>
        <v-sheet :height="75" class="d-flex">
          <v-row class="pl-1 align-center" no-gutters>
            <v-col :cols="3" :md="2" class="py-0">
              <v-select
                :items="languages"
                outlined
                class="ml-0"
                hide-details
                v-model="language"
                @change="saveEditorConfig"
              >
              </v-select>
            </v-col>
            <v-spacer class="hidden-sm-and-down"></v-spacer>
            <v-col :cols="1" align="right">
              <v-tooltip left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon color="accent" large v-bind="attrs" v-on="on">
                    <v-icon class="text-right" @click="removeCode"
                      >mdi-cached</v-icon
                    >
                  </v-btn>
                </template>
                <span>Reset Code</span>
              </v-tooltip>
            </v-col>
            <v-col :cols="8" :md="7" :lg="6" class="py-0 pr-1">
              <v-row no-gutters>
                <v-col :cols="6" :md="3" class="py-0 pl-1">
                  <v-select
                    :items="themes"
                    outlined
                    class="ml-0 font-weight-light"
                    hide-details
                    v-model="theme"
                    @change="saveEditorConfig"
                  >
                  </v-select>
                </v-col>
                <v-col :cols="6" :md="3" class="py-0 pl-1">
                  <v-select
                    :items="keyMaps"
                    outlined
                    class="ml-0 font-weight-light"
                    hide-details
                    v-model="keyMap"
                    @change="saveEditorConfig"
                  >
                  </v-select>
                </v-col>
                <v-col :cols="3" class="py-0 pl-1 hidden-sm-and-down">
                  <v-select
                    :items="fontSizes"
                    outlined
                    class="ml-0 font-weight-light"
                    hide-details
                    v-model="fontSize"
                    @change="saveEditorConfig"
                  >
                  </v-select>
                </v-col>
                <v-col :cols="3" class="py-0 pl-1 hidden-sm-and-down">
                  <v-select
                    :items="tabSizes"
                    outlined
                    class="ml-0 font-weight-light"
                    hide-details
                    v-model="tabSize"
                    @change="saveEditorConfig"
                  >
                  </v-select>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-sheet>
        <v-divider></v-divider>
        <v-row>
          <v-col class="py-0">
            <CodeMirror
              ref="myCm"
              :value="value"
              @input="onCodeChange"
              :options="cmOptions"
              :class="fontSize"
            ></CodeMirror>
          </v-col>
        </v-row>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { mapActions, mapState, mapMutations, mapGetters } from "vuex";
import { codemirror as CodeMirror } from "vue-codemirror";
import "@/assets/css/codemirror.css";
import "codemirror/mode/python/python.js";
import "codemirror/mode/clike/clike.js";
import "codemirror/mode/sql/sql.js";
import "codemirror/mode/javascript/javascript.js";
import "codemirror/mode/ruby/ruby.js";
import "codemirror/mode/go/go.js";
import "codemirror/theme/dracula.css";
import "codemirror/theme/solarized.css";
import "codemirror/theme/idea.css";
import "codemirror/keymap/emacs.js";
import "codemirror/keymap/vim.js";
import "codemirror/keymap/sublime.js";
import "codemirror/lib/codemirror";
import "codemirror/mode/dart/dart.js";
import "codemirror/mode/r/r.js";
import "codemirror/mode/php/php.js";

export default {
  data() {
    return {
      showHint: false,
      tabSize: 4,
      theme: "default",
      language: "CPP_17",
      keyMap: "default",
      fontSize: "normal",
      languages: [
        { text: "C++", value: "CPP_17" },
        { text: "Java", value: "JAVA_11" },
        { text: "Kotlin", value: "KOTLIN_1_3" },
        { text: "Python", value: "PYTHON_3" },
        { text: "MySQL 8", value: "MY_SQL_8" },
        { text: "JavaScript", value: "JAVASCRIPT" },
        { text: "Ruby", value: "RUBY" },
        { text: "C#", value: "CSHARP" },
        { text: "TypeScript", value: "TYPESCRIPT" },
        { text: "Go", value: "GO" },
        { text: "Dart", value: "DART" },
        { text: "R", value: "R" },
        { text: "PHP", value: "PHP" },
      ],
      themes: [
        { text: "Idea", value: "idea" },
        { text: "Dracula", value: "dracula" },
        { text: "Solarized Dark", value: "solarized dark" },
        { text: "Solarized Light", value: "solarized light" },
        { text: "Default", value: "default" },
      ],
      keyMaps: [
        { text: "Vim", value: "vim" },
        { text: "Emacs", value: "emacs" },
        { text: "Sublime", value: "sublime" },
        { text: "Basic", value: "default" },
      ],
      fontSizes: [
        { text: "Small", value: "small" },
        { text: "Normal", value: "normal" },
        { text: "Large", value: "large" },
      ],
      tabSizes: [
        { text: "2 Spaces", value: 2 },
        { text: "4 Spaces", value: 4 },
      ],
      cmModeMap: {
        CPP_17: "text/x-c++src",
        JAVA_11: "text/x-java",
        KOTLIN_1_3: "text/x-kotlin",
        PYTHON_3: "text/x-python",
        JAVASCRIPT: "text/javascript",
        MY_SQL_8: "text/x-mysql",
        RUBY: "text/x-ruby",
        CSHARP: "text/x-csharp",
        TYPESCRIPT: "text/typescript",
        GO: "text/x-go",
        DART: "dart",
        R: "text/x-rsrc",
        PHP: "text/x-php",
      },
    };
  },
  props: {
    value: {
      type: String,
      required: false,
    },
  },
  components: {
    CodeMirror,
  },
  computed: {
    ...mapState("user", ["userPreference", "user"]),
    ...mapGetters("user", ["languageKeys"]),
    codemirror() {
      return this.$refs.myCm.codemirror;
    },
    editorConfig() {
      return this.userPreference && this.userPreference.getEditorConfig();
    },
    cmOptions() {
      return {
        tabSize: this.tabSize,
        /*indentUnit: 4,*/
        indentWithTabs: true,
        mode: this.cmModeMap[this.language],
        lineNumbers: true,
        line: true,
        styleActiveLine: true,
        matchBrackets: true,
        lineWrapping: true,
        theme: this.theme,
        foldGutter: true,
        keyMap: this.keyMap,
      };
    },
  },
  watch: {
    editorConfig: function (config) {
      console.log("config change", config);
      if (!config) return;
      this.language = this.languageKeys[config.getLanguage()] || "CPP_17";
      this.tabSize = config.getTabSize() || 4;
      this.theme = config.getTheme() || "default";
      this.keyMap = config.getKeyMap() || "default";
      this.fontSize = config.getFontSize() || "normal";
    },
    defaultCode: function (newDefaultCode) {
      this.value = newDefaultCode;
    },
    language: function (newLanguage) {
      this.$emit("setLanguage", newLanguage);
    },
  },
  methods: {
    ...mapMutations("user", ["setEditorConfig"]),
    ...mapActions("user", ["saveUserPreference", "getUserPreference"]),
    onCodeChange(newValue) {
      this.$emit("input", newValue);
    },
    saveEditorConfig() {
      this.setEditorConfig({
        language: this.language,
        tabSize: this.tabSize,
        theme: this.theme,
        keyMap: this.keyMap,
        fontSize: this.fontSize,
      });
      if (this.user) {
        this.saveUserPreference();
      }
    },
    removeCode() {
      this.$emit("resetCode");
    },
  },
  mounted() {
    this.$emit("setLanguage", this.language);
    if (this.user) {
      this.getUserPreference();
    }
  },
};
</script>
